import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Bell, Check } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { notificationsAPI } from '../services/api'

// mobiel 2026-09-10 (audit NotificationBell.jsx:128): onder 1024 px staat de bel in de mobiele
// dashboard-header (backdrop-blur + z-30). Een absolute dropdown aan de bel valt op 360 px 16 px links
// buiten beeld, en z-[60] werkt alleen binnen die z-30 context. Daarom op mobiel: paneel via portal op
// document.body als fixed balk onder de header. Desktop (sidebar, ≥1024 px) blijft absolute zoals het was.
const MOBILE_QUERY = '(max-width: 1023px)'
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const onChange = (e) => setIsMobile(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])
  return isMobile
}

const TYPE_EMOJI = {
  proposal_received: '📩',
  proposal_accepted: '✅',
  proposal_rejected: '❌',
  invitation_received: '💌',
  invitation_response: '↩️',
  message_received: '💬',
  deliverable_submitted: '🎬',
  deliverable_approved: '👍',
  revision_requested: '✏️',
  payment_update: '💰',
  project_completed: '🏁',
  system: '🔔',
}

const timeAgo = (date) => {
  if (!date) return ''
  const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (s < 60) return 'zojuist'
  const m = Math.floor(s / 60); if (m < 60) return `${m} min geleden`
  const h = Math.floor(m / 60); if (h < 24) return `${h} uur geleden`
  const d = Math.floor(h / 24); if (d < 7) return `${d} dag${d === 1 ? '' : 'en'} geleden`
  return new Date(date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
}

/**
 * Notificatie-bel met ongelezen-badge en dropdown.
 * Polt het ongelezen-aantal lichtgewicht; haalt de lijst pas op bij openen.
 *
 * Props:
 *  - accent: themakleur (string), default groen
 *  - onNavigate: optionele (view, id) => void van het dashboard (switchView).
 *    Wordt gebruikt om in-app naar project-proposals te springen i.p.v. routeren.
 */
const NotificationBell = ({ accent = '#3EB489', onNavigate }) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)
  const panelRef = useRef(null) // mobiel 2026-09-10: paneel zit via portal buiten `ref`
  const isMobile = useIsMobile()

  const fetchCount = useCallback(async () => {
    try {
      const { data } = await notificationsAPI.unreadCount()
      setUnread(data.unreadCount || 0)
    } catch { /* stil — bel mag nooit de UI breken */ }
  }, [])

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await notificationsAPI.list({ limit: 15 })
      setItems(data.notifications || [])
      setUnread(data.unreadCount || 0)
    } catch { /* stil */ } finally { setLoading(false) }
  }, [])

  // Lichtgewicht polling van het aantal (elke 45s) + bij terugkeer naar tab
  useEffect(() => {
    fetchCount()
    const iv = setInterval(fetchCount, 45000)
    const onVis = () => { if (!document.hidden) fetchCount() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis) }
  }, [fetchCount])

  // Sluiten bij klik of tik buiten bel én paneel.
  // mobiel 2026-09-10 (audit NotificationBell.jsx:74-79): ook touchstart, want op touch komt mousedown pas
  // na de tap-afhandeling en een scroll-veeg buiten het paneel sloot het niet.
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return
      if (panelRef.current && panelRef.current.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('touchstart', onDoc, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('touchstart', onDoc)
    }
  }, [open])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) fetchList()
  }

  const goTo = (n) => {
    // In-app navigeren als het dashboard switchView aanlevert en het een project-link is
    if (onNavigate && n.project && /\/proposals$/.test(n.link || '')) {
      onNavigate('project-proposals', n.project)
    } else if (n.link) {
      navigate(n.link)
    }
  }

  const handleClick = async (n) => {
    setOpen(false)
    if (!n.read) {
      setUnread(u => Math.max(0, u - 1))
      setItems(list => list.map(x => x._id === n._id ? { ...x, read: true } : x))
      notificationsAPI.markRead(n._id).catch(() => {})
    }
    goTo(n)
  }

  const markAll = async () => {
    setUnread(0)
    setItems(list => list.map(x => ({ ...x, read: true })))
    try { await notificationsAPI.markAllRead() } catch { /* stil */ }
  }

  // mobiel 2026-09-10: op <1024 px fixed balk onder de mobiele header (61 px) met 12 px zijmarge, via portal
  // (buiten de z-30 stacking context van de header); desktop ongewijzigd absolute onder de bel in de sidebar.
  const panelClass = isMobile
    ? 'fixed left-3 right-3 top-[68px] z-[60] bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden'
    : 'absolute right-0 lg:right-auto lg:left-0 mt-2 w-[320px] max-w-[calc(100vw-32px)] bg-white border border-gray-200 rounded-2xl shadow-xl z-[60] overflow-hidden'

  const panel = (
    <div ref={panelRef} className={panelClass}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span className="text-sm font-bold text-[#0F172A]">Meldingen</span>
        {unread > 0 && (
          <button onClick={markAll} className="text-xs font-semibold flex items-center gap-1 py-2 -my-2 transition hover:opacity-70" style={{ color: accent }}>
            <Check className="w-3.5 h-3.5" /> Alles gelezen
          </button>
        )}
      </div>

      <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-[#94A3B8]">Laden…</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="text-3xl mb-2">🔔</div>
            <p className="text-sm text-[#475569]">Nog geen meldingen</p>
          </div>
        ) : (
          items.map((n) => (
            <button
              key={n._id}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-3 flex gap-3 border-b border-gray-50 hover:bg-gray-50 transition ${n.read ? '' : 'bg-emerald-50/40'}`}
            >
              <span className="text-lg leading-none flex-shrink-0 mt-0.5" aria-hidden="true">{TYPE_EMOJI[n.type] || '🔔'}</span>
              <span className="flex-1 min-w-0">
                {/* mobiel 2026-09-10 (audit :155-156): titel mag afbreken, body 2 regels i.p.v. 1 regel truncate */}
                <span className="block text-sm font-semibold text-[#0F172A] break-words">{n.title}</span>
                {n.body && <span className="block text-xs text-[#475569] line-clamp-2 break-words">{n.body}</span>}
                <span className="block text-[11px] text-[#94A3B8] mt-0.5">{timeAgo(n.createdAt)}</span>
              </span>
              {!n.read && <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: accent }} />}
            </button>
          ))
        )}
      </div>
    </div>
  )

  return (
    <div className="relative" ref={ref}>
      {/* mobiel 2026-09-10 (audit :114-119): p-2.5 = 44 px tikdoel op mobiel; desktop houdt p-2 */}
      <button
        onClick={toggle}
        className="p-2.5 lg:p-2 hover:bg-gray-100 rounded-lg relative transition"
        aria-label="Notificaties"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5 text-[#475569]" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 lg:-top-0.5 lg:-right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (isMobile ? createPortal(panel, document.body) : panel)}
    </div>
  )
}

export default NotificationBell
